import { Controller } from "@hotwired/stimulus"
import { showBlock, check_auth_status } from "lib/helpers"

const successBlock = "#update-email-notification"
const errorBlock = ".update-email-notification-errors"
const resetSecretErrorBlock = "#confirm-reset-secret-errors"

// chat--offline-alerts on email notifications in `chat/alerts/index`.
export default class extends Controller {
  static targets = ["form", "enabledCheckbox", "settingsBox", "signingSecret", "confirmationModal", "confirmForm"]

  connect() {
    if (this.hasFormTarget) this.#initOfflineEmailNotificationForm()
    if (this.hasConfirmFormTarget) this.#initConfirmResetSigningSecretForm()
  }

  disconnect() {
    if (this.hasFormTarget) $(this.formTarget).ajaxFormUnbind()
    if (this.hasConfirmFormTarget) $(this.confirmFormTarget).ajaxFormUnbind()
  }

  toggleWebhook() {
    if (!this.hasEnabledCheckboxTarget || !this.hasSettingsBoxTarget) return

    const enabled = this.enabledCheckboxTarget.checked
    this.settingsBoxTarget.classList.toggle("none-info-box", !enabled)
    this.settingsBoxTarget.querySelectorAll("input, button").forEach((el) => {
      if (el.dataset.keepDisabled === "true") {
        el.disabled = true
        return
      }
      el.disabled = !enabled
    })
  }

  resetSigningSecret(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.url
    const method = (button.dataset.method || "patch").toUpperCase()
    if (!url) return

    fetch(url, {
      method,
      headers: this.#jsonHeaders(),
      credentials: "same-origin",
    })
      .then((response) => this.#parseJsonResponse(response))
      .then(({ response, payload }) => {
        if (payload?.success) {
          if (this.hasConfirmationModalTarget) $(this.confirmationModalTarget).modal("show")
        } else {
          showBlock(errorBlock, payload?.message || "An error occurred during request")
        }
        check_auth_status(response.status)
      })
      .catch(() => {
        showBlock(errorBlock, "An error occurred during request")
      })
  }

  copy(event) {
    event.preventDefault()

    const inputId = event.currentTarget.id.substring(2)
    const input = document.getElementById(inputId)
    if (!input) return

    input.select()
    document.execCommand("copy")
  }

  preventPaste(event) {
    event.preventDefault()
  }

  #initOfflineEmailNotificationForm() {
    $(this.formTarget).ajaxForm({
      dataType: "json",
      success: (response) => {
        if (response.success) {
          showBlock(successBlock, response.message)
        } else {
          // showBlock removes closed alerts from the DOM, so keep a template copy available.
          const error = $(errorBlock)
          if (error.length === 1) {
            error.after(error.clone())
          }
          showBlock(errorBlock, response.message)
        }
      },
      error: (response) => {
        showBlock(errorBlock, "An error occurred during request")
        check_auth_status(response.status)
      },
    })
  }

  #initConfirmResetSigningSecretForm() {
    const form = $(this.confirmFormTarget)
    form.ajaxForm({
      dataType: "json",
      resetForm: true,
      success: (response) => {
        if (response.success) {
          if (this.hasSigningSecretTarget) this.signingSecretTarget.value = response.signing_secret
          if (this.hasConfirmationModalTarget) $(this.confirmationModalTarget).modal("hide")
          showBlock(successBlock, response.message)
        } else {
          showBlock(resetSecretErrorBlock, response.message)
        }
        form.find("input[type='submit']").enable(true)
      },
      error: (response) => {
        showBlock(resetSecretErrorBlock, "An error occurred during request")
        check_auth_status(response.status)
      },
    })
  }

  #jsonHeaders() {
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    return {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrf || "",
    }
  }

  async #parseJsonResponse(response) {
    let payload
    try {
      payload = await response.json()
    } catch (_error) {
      payload = null
    }
    return { response, payload }
  }
}
